import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { TierBadge } from '@/components/dashboard/TierBadge';
import { cn, timeAgo } from '@/lib/utils';
import api from '@/lib/api';
import {
  Brain,
  CheckCircle2,
  XCircle,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Target,
  TrendingUp,
  AlertCircle,
  Loader2,
  ChevronRight,
  Layers,
  Zap
} from 'lucide-react';

export function IntentTrainingPage() {
  const [intents, setIntents] = useState(null);
  const [pendingFeedback, setPendingFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testQuery, setTestQuery] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newExample, setNewExample] = useState({ intent: '', text: '', tier: 'tier2_local_smart' });
  const [selectedIntent, setSelectedIntent] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [intentsRes, feedbackRes, statsRes] = await Promise.all([
        api.getIntents(),
        api.getPendingFeedback(),
        api.getFeedbackStats()
      ]);
      setIntents(intentsRes.data);
      setPendingFeedback(feedbackRes.data.feedback || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTestClassification = async () => {
    if (!testQuery.trim()) return;
    setTestLoading(true);
    try {
      const response = await api.testClassification(testQuery);
      setTestResult(response.data);
    } catch (err) {
      console.error('Test failed:', err);
    } finally {
      setTestLoading(false);
    }
  };

  const handleApprove = async (feedbackId) => {
    try {
      await api.approveFeedback(feedbackId);
      setPendingFeedback(prev => prev.filter(f => f.id !== feedbackId));
      fetchData(); // Refresh stats
    } catch (err) {
      console.error('Approve failed:', err);
    }
  };

  const handleReject = async (feedbackId) => {
    try {
      await api.rejectFeedback(feedbackId);
      setPendingFeedback(prev => prev.filter(f => f.id !== feedbackId));
      fetchData();
    } catch (err) {
      console.error('Reject failed:', err);
    }
  };

  const handleAddExample = async () => {
    if (!newExample.intent || !newExample.text) return;
    try {
      await api.addIntentExample(newExample);
      setAddDialogOpen(false);
      setNewExample({ intent: '', text: '', tier: 'tier2_local_smart' });
      fetchData();
    } catch (err) {
      console.error('Add example failed:', err);
    }
  };

  const intentList = intents?.intents ? Object.values(intents.intents) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="intent-training-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">
            INTENT TRAINING
          </h1>
          <p className="text-muted-foreground mt-1">
            Improve routing accuracy through online learning
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-example-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Example
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Training Example</DialogTitle>
                <DialogDescription>
                  Add a new example to improve intent classification
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Intent</Label>
                  <Select
                    value={newExample.intent}
                    onValueChange={(v) => setNewExample(prev => ({ ...prev, intent: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select intent" />
                    </SelectTrigger>
                    <SelectContent>
                      {intentList.map(intent => (
                        <SelectItem key={intent.name} value={intent.name}>
                          {intent.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Example Text</Label>
                  <Textarea
                    value={newExample.text}
                    onChange={(e) => setNewExample(prev => ({ ...prev, text: e.target.value }))}
                    placeholder="Enter an example query for this intent..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Target Tier</Label>
                  <Select
                    value={newExample.tier}
                    onValueChange={(v) => setNewExample(prev => ({ ...prev, tier: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tier1_local_fast">Tier 1 - Local Fast</SelectItem>
                      <SelectItem value="tier2_local_smart">Tier 2 - Local Smart</SelectItem>
                      <SelectItem value="tier3_cloud">Tier 3 - Cloud</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddExample} disabled={!newExample.intent || !newExample.text}>
                  Add Example
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-border bg-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-primary/10">
                <Layers className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{intents?.total_intents || 0}</p>
                <p className="text-xs text-muted-foreground">Intent Classes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-emerald-500/10">
                <Sparkles className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{intents?.total_examples || 0}</p>
                <p className="text-xs text-muted-foreground">Training Examples</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-amber-500/10">
                <AlertCircle className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.pending || 0}</p>
                <p className="text-xs text-muted-foreground">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-blue-500/10">
                <TrendingUp className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.approved || 0}</p>
                <p className="text-xs text-muted-foreground">Improvements Made</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="test" className="space-y-4">
        <TabsList>
          <TabsTrigger value="test" className="gap-2">
            <Search className="h-4 w-4" />
            Test Classifier
          </TabsTrigger>
          <TabsTrigger value="review" className="gap-2">
            <Target className="h-4 w-4" />
            Review Feedback
            {pendingFeedback.length > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 px-1.5">
                {pendingFeedback.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="intents" className="gap-2">
            <Brain className="h-4 w-4" />
            All Intents
          </TabsTrigger>
        </TabsList>

        {/* Test Classifier Tab */}
        <TabsContent value="test" className="space-y-4">
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Test Classification</CardTitle>
              <CardDescription>
                Enter a query to see how it would be classified
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={testQuery}
                  onChange={(e) => setTestQuery(e.target.value)}
                  placeholder="Enter a test query..."
                  onKeyDown={(e) => e.key === 'Enter' && handleTestClassification()}
                  data-testid="test-query-input"
                />
                <Button onClick={handleTestClassification} disabled={testLoading || !testQuery.trim()}>
                  {testLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Zap className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {testResult && (
                <div className="p-4 rounded-lg bg-secondary/50 border border-border space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Predicted Intent</p>
                      <p className="text-xl font-bold text-primary">{testResult.predicted_intent}</p>
                    </div>
                    <TierBadge tier={testResult.predicted_tier} />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Confidence</p>
                      <p className="font-mono">{(testResult.confidence * 100).toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Complexity</p>
                      <p className="font-mono">{(testResult.complexity * 100).toFixed(0)}%</p>
                    </div>
                  </div>

                  {testResult.top_matches && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Top Matches:</p>
                      <div className="flex flex-wrap gap-2">
                        {testResult.top_matches.map(([intent, score], idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {intent}: {(score * 100).toFixed(0)}%
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Review Feedback Tab */}
        <TabsContent value="review" className="space-y-4">
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Pending Corrections</CardTitle>
              <CardDescription>
                Review and approve user-submitted corrections to improve the classifier
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingFeedback.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No pending feedback to review</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {pendingFeedback.map((feedback) => (
                      <div
                        key={feedback.id}
                        className="p-4 rounded-lg bg-secondary/30 border border-border"
                      >
                        <p className="text-sm mb-3 font-medium">"{feedback.query}"</p>
                        
                        <div className="flex items-center gap-4 mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Predicted:</span>
                            <Badge variant="outline">{feedback.predicted_intent}</Badge>
                            <TierBadge tier={feedback.predicted_tier} size="sm" />
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Corrected:</span>
                            <Badge className="bg-primary/20 text-primary border-primary/30">
                              {feedback.corrected_intent}
                            </Badge>
                            <TierBadge tier={feedback.corrected_tier} size="sm" />
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {timeAgo(feedback.created_at)}
                          </span>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReject(feedback.id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleApprove(feedback.id)}
                              className="bg-emerald-600 hover:bg-emerald-500"
                            >
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Intents Tab */}
        <TabsContent value="intents" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {intentList.map((intent) => (
              <Card 
                key={intent.name} 
                className={cn(
                  "border-border bg-card cursor-pointer transition-all hover:border-primary/50",
                  selectedIntent === intent.name && "border-primary"
                )}
                onClick={() => setSelectedIntent(selectedIntent === intent.name ? null : intent.name)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium capitalize">
                      {intent.name.replace(/_/g, ' ')}
                    </CardTitle>
                    <TierBadge tier={intent.tier} size="sm" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{intent.total_examples} examples</span>
                    <span>
                      {intent.custom_examples?.length || 0} custom
                    </span>
                  </div>
                  
                  {selectedIntent === intent.name && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-xs font-medium mb-2">Example queries:</p>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {intent.default_examples?.slice(0, 5).map((ex, idx) => (
                          <p key={idx} className="text-xs text-muted-foreground truncate">
                            • {ex}
                          </p>
                        ))}
                        {intent.custom_examples?.map((ex, idx) => (
                          <p key={`custom-${idx}`} className="text-xs text-emerald-400 truncate">
                            • {ex.text} <span className="text-[10px]">(custom)</span>
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default IntentTrainingPage;
